import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AdjacentType, DeviceState, GatewayState } from '@iotv/datamodel-core';
import { AppValueTypes, ComparisonType, LogicalOperatorType } from '@iotv/iotv-v3-types';
import { Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LoadingAnimationModal, StaticLoadingAnimation } from '../../../Popup/Loading';
import AppDao from '../../../data/AppDao';
import { getItems } from '../../../data/Wideawake/wideawakeDataFns';
import { unlink } from '../../../data/daoFunctions/daoFunctions';
import { inBoth } from '../../../helpers/filters';
import { useSelectedObjects } from '../../../hooks/useSelectedObjects';
import { ComponentMode, ObjectViewType } from '../../../types/AppTypes';
import { ListerlizerAdapter } from '../../factories/Listerlizer/legacy/ListerlizerAdapter';
const getGateways = async () => {
    const res = await getItems({ objectTypeId: 'Gateway' });
    if (res.err === null && res.data) {
        const allItems = res.data.Items;
        const items = allItems.filter((item) => item.state === GatewayState.WITHCUSTOMER || item.state === GatewayState.COMMISSIONED);
        return items;
    }
    else {
        return [];
    }
};
/** @deprecated - No longer used */
const GatewayUnassignmentCard = (props) => {
    const { userFunctions, transactionFunctions } = props;
    const { state: [[selectedObjects, setSelectedObjects]], } = useSelectedObjects();
    const [gateways, setGateways] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [filteredGateways, setFilteredGateways] = useState([]);
    const [connectedGateways, setConnectedGateways] = useState([]);
    const [foundCustomers, setFoundCustomers] = useState([]);
    const [gatewayToCustomerMapper, setGatewayToCustomerMapper] = useState(undefined);
    const getGatewaysListerlizer = (context) => {
        const baseQueryFilter = [
            {
                filters: [
                    {
                        key: 'state',
                        value: DeviceState.INVENTORY,
                        predicate: ComparisonType.EQUALS,
                    },
                ],
                setOperation: LogicalOperatorType.AND,
            },
        ];
        return {
            initMode: ComponentMode.EDIT,
            transactionFunctions,
            userFunctions,
            adjacentFilter: {
                adjacentType: AdjacentType.CHILD,
                objectType: 'Gateway',
                filterConfig: baseQueryFilter,
            },
            autoQuery: true,
            columnVisibilityReferences: {
                dataTableReference: 'List',
                objectTypeId: 'ValidBusinessObject',
                viewType: ObjectViewType.List,
                externalColumnVisibility: undefined,
            },
            viewDefinition: {
                name: {
                    key: 'name',
                    type: AppValueTypes.STRING,
                    label: 'Gateway',
                    editable: false,
                    precision: undefined,
                    stateFn: undefined,
                    unit: undefined,
                    validationRx: undefined,
                    enumKV: undefined,
                },
                id: {
                    key: 'id',
                    type: AppValueTypes.STRING,
                    label: 'EUI',
                    editable: false,
                    precision: undefined,
                    stateFn: undefined,
                    unit: undefined,
                    validationRx: undefined,
                    enumKV: undefined,
                },
                group: {
                    key: 'group',
                    type: AppValueTypes.STRING,
                    label: 'Gateway Group',
                    editable: false,
                    precision: undefined,
                    stateFn: undefined,
                    unit: undefined,
                    validationRx: undefined,
                    enumKV: undefined,
                },
                customer: {
                    key: 'customer',
                    type: AppValueTypes.STRING,
                    label: 'Customer',
                    editable: false,
                    precision: undefined,
                    stateFn: undefined,
                    unit: undefined,
                    validationRx: undefined,
                    enumKV: undefined,
                },
                lastSeen: {
                    key: 'lastSeen',
                    type: AppValueTypes.DATE,
                    label: 'Last Seen',
                    editable: false,
                    precision: undefined,
                    stateFn: undefined,
                    unit: undefined,
                    validationRx: undefined,
                    enumKV: undefined,
                },
            },
            viewObject: {
                matchedPrimary: undefined,
                matchedRelatedByPk: [],
                matchedRelatedBySk: context,
            },
            contextObject: undefined,
            searchRootObject: undefined,
            onSelectedUpdate: setSelectedObjects,
        };
    };
    const renderGatewayList = () => {
        if (gateways) {
            getHydratedGateways(gateways).then((newGateway) => {
                setFilteredGateways(newGateway);
            });
        }
    };
    const updateGateways = () => {
        getGateways().then((res) => {
            setGateways(res);
        });
    };
    useEffect(() => {
        updateGateways();
    }, []);
    useEffect(() => {
        renderGatewayList();
    }, [gateways]);
    const getHydratedGateways = async (gatewayList) => {
        const mapperPromise = await AppDao.queryByGraph({
            contextObjects: gatewayList,
            path: [
                { objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT },
                { objectTypeId: 'Customer', adjacencyType: AdjacentType.PARENT },
                { objectTypeId: 'Group', adjacencyType: AdjacentType.PARENT },
            ],
        });
        if (mapperPromise.err === null && mapperPromise.data !== null) {
            const mapper = mapperPromise.data;
            setGatewayToCustomerMapper(mapper);
            const allGroups = mapper.getNodesOfType('Group');
            // get customer groups (top customer does no belong to a group)
            const customerGroups = allGroups.filter((group) => group.supportedType === 'Customer');
            const customers = customerGroups
                .map((customerGroup) => {
                return mapper.getChildNodes(customerGroup);
            })
                .flat();
            const gatewayGroups = customers
                .map((customer) => {
                return mapper.getChildNodes(customer);
            })
                .flat();
            setConnectedGateways(gatewayGroups);
            setFoundCustomers(customers);
            // get gateways parent twice, filter by gateway groups and customer
            const formattedGateways = gatewayList.map((gateway) => {
                let groups = mapper.getParentNodes(gateway);
                groups = inBoth(groups, gatewayGroups, 'id');
                const customers = groups
                    .map((group) => {
                    return mapper.getParentNodes(group);
                })
                    .flat();
                const customer = [...new Set(customers)].map((gateway) => gateway.name).join(', ');
                return {
                    ...gateway,
                    group: groups.map((group) => group.name).join(', '),
                    customer,
                };
            });
            return formattedGateways;
        }
        else {
            return gatewayList;
        }
    };
    const unassignGateways = async () => {
        if (gatewayToCustomerMapper !== undefined) {
            setProcessing(true);
            const allPromises = selectedObjects
                .map((selected) => {
                // get gateways groups that are not customers
                const groups = gatewayToCustomerMapper.getParentNodes(selected);
                const relevantGroups = inBoth(groups, connectedGateways, 'id');
                const unlinkPromises = relevantGroups.map((group) => {
                    return unlink(group, selected);
                });
                return unlinkPromises;
            })
                .flat();
            selectedObjects.forEach((selected) => {
                return AppDao.getExact(selected).then((fromDB) => {
                    if (fromDB.data?.Items !== undefined && fromDB.data.Items.length === 1) {
                        const exact = fromDB?.data?.Items[0];
                        AppDao.save({ ...exact, state: GatewayState.INVENTORY }).then(() => {
                            updateGateways();
                        });
                    }
                });
            });
            Promise.all(allPromises).then((res) => {
                setProcessing(false);
            });
        }
    };
    return (_jsxs("div", { children: [_jsx(Typography, { component: "h6", variant: "h6", children: "Unassign Gateways from Customer" }), _jsx(Button, { variant: "contained", color: "primary", disabled: selectedObjects.length === 0, onClick: unassignGateways, children: "Unassign" }), _jsx("br", {}), _jsx("br", {}), gateways ? _jsx(ListerlizerAdapter, { ...getGatewaysListerlizer(filteredGateways) }) : _jsx(StaticLoadingAnimation, { message: 'Retrieving Gateway Details' }), processing && _jsx(LoadingAnimationModal, { message: 'Processing Unassignment' })] }));
};
export default GatewayUnassignmentCard;
